import React, { useEffect, useRef, useState } from "react";

// Blinking block cursor
export function Cursor({ className = "" }) {
  return (
    <span className={`text-mizu-cyan animate-blink ${className}`} aria-hidden>
      _
    </span>
  );
}

// Types an array of lines sequentially, then keeps a blinking cursor.
// speed: ms per char. lineDelay: ms between lines.
export function Typewriter({
  lines,
  speed = 14,
  lineDelay = 140,
  startDelay = 0,
  className = "",
  lineClassName = "",
  onDone,
  loop = false,
}) {
  const [done, setDone] = useState([]);
  const [current, setCurrent] = useState("");
  const [finished, setFinished] = useState(false);
  const stateRef = useRef({ line: 0, char: 0, timer: null });
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    let cancelled = false;
    const key = lines.join("\n");

    const run = () => {
      if (cancelled) return;
      const s = stateRef.current;
      if (s.line >= lines.length) {
        setFinished(true);
        if (onDone) onDone();
        if (loop) {
          s.timer = setTimeout(() => {
            if (cancelled) return;
            s.line = 0;
            s.char = 0;
            setDone([]);
            setCurrent("");
            setFinished(false);
            s.timer = setTimeout(tick, 600);
          }, 4200);
        }
        return;
      }
      setCurrent(lines[s.line].slice(0, s.char));
      s.char += 1;
      if (s.char > lines[s.line].length) {
        setDone((d) => [...d, lines[s.line]]);
        setCurrent("");
        s.line += 1;
        s.char = 0;
        s.timer = setTimeout(tick, lineDelay);
      } else {
        s.timer = setTimeout(tick, speed);
      }
    };

    const tick = () => run();

    // reset when lines change (language switch)
    stateRef.current.line = 0;
    stateRef.current.char = 0;
    setDone([]);
    setCurrent("");
    setFinished(false);

    if (reduced) {
      setDone(lines);
      setFinished(true);
      if (onDone) onDone();
      return () => {};
    }

    stateRef.current.timer = setTimeout(tick, startDelay);
    return () => {
      cancelled = true;
      clearTimeout(stateRef.current.timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines.join("\n"), loop]);

  return (
    <div className={className} data-testid="typewriter">
      {done.map((l, i) => (
        <div key={i} className={lineClassName}>
          {l}
        </div>
      ))}
      {!finished ? (
        <div className={lineClassName}>
          {current}
          <Cursor />
        </div>
      ) : (
        <Cursor />
      )}
    </div>
  );
}

export default Typewriter;
